import { Box, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import React, { Fragment, useState } from 'react';
import { docGeniusPalette } from 'styles/partials/colors';
import { AzulSkeleton } from '@the-ksquare-group/azul-ds';
import { CardListPreview } from 'components/CardListPreview';
import { DescriptionRounded, ViewList, GridViewRounded, FileCopy, Visibility, Delete } from '@mui/icons-material';
import {
  useGetDocumentsQuery,
  useGetSelfDocumentsQuery,
  usePatchDocumentMutation,
} from 'features/document/documentApi';
import RadioTag, { Tag } from 'components/RadioTag/RadioTag';
import { QueryBuilder } from 'utils/formatQueryParams';
import EmptyTable from 'components/DataTable/components/EmptyTable/EmptyTable';
import DocumentTable from './components/DocumentsTable/DocumentsTable';
import { useNavigate } from 'react-router-dom';
import styles from './DocumentsPage.module.css';
import DetailsModal from './components/DetailsModal/DetailsModal';
import { SearchBar } from 'components/SearchBar';
import ModalFactory, { ModalType } from 'components/CustomModal/ModalFactory';
import { MenuOption } from 'dataTypes/document';
import { DocumentType } from 'features/document/documentSlice';
import { documentStatus } from 'features/document/constants';
import NewFileButton from 'components/NewFileButton/NewFileButton';
import { useAppSelector } from 'app/hooks';
import { selectSupportedDocTypes } from 'features/organization/organizationSlice';
import { selectIsSalesforceAuth } from 'features/salesforce/salesforceSlice';

export function DocumentsPage() {
  const navigate = useNavigate();
  const [searchBy, setSearchBy] = useState('');
  const [isList, setIsList] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<ModalType>('delete');
  const [modalData, setModalData] = useState({});
  const [documentInfo, setDocumentInfo] = useState<{ id: number | null; documentName: string }>({
    id: null,
    documentName: '',
  });
  const [selectedTag, setSelectedTag] = useState<Tag>({
    value: 'all',
    label: 'All Files',
  });
  const [patchDocument] = usePatchDocumentMutation();

  const toggleFavorite = async (
    event: React.MouseEvent<HTMLButtonElement>,
    id: number,
    name: string,
    isDocFavorite: boolean
  ) => {
    event.stopPropagation();
    event.currentTarget.blur();
    try {
      await patchDocument({ id, name, isFavorite: !isDocFavorite }).unwrap();
    } catch (error) {
      console.error(error);
    }
  };

  function handleOpenDocument(id: number, type: string, status: string) {
    if (type) {
      if (status === 'active' || status === 'draft' || status === 'created') {
        navigate(`/documents/edit/${type.toLowerCase()}/${id}`);
      } else {
        navigate(`/documents/view/${type.toLowerCase()}/${id}`);
      }
    } else {
      navigate(`/documents/edit/document/${id}`);
    }
  }

  const onHandleTagChange = (tag: Tag) => {
    setSelectedTag(tag);
  };
  const handleSearch = (query: string) => {
    setSearchBy(query);
  };
  const builder = new QueryBuilder();
  const queryString = builder
    .setQueryInclude({ field: 'files' })
    .setQueryWhere({ field: 'name', operator: '$substring', value: searchBy })
    .setQueryWhere({
      field: 'isFavorite',
      operator: '$eq',
      value: selectedTag.value === 'favorites' ? 'true' : undefined,
    })
    .setQueryOrder({
      field: 'statusUpdated',
      order: selectedTag.value === 'recentlyOpened' ? 'ASC' : undefined,
    })
    .setQueryWhere({
      field: 'type',
      operator: '$eq',
      value: selectedTag.value === 'document' ? DocumentType.DOCUMENT : undefined,
    })
    .setQueryWhere({
      field: 'type',
      operator: '$eq',
      value: selectedTag.value === 'quote' ? DocumentType.QUOTE : undefined,
    })
    .setQueryWhere({
      field: 'type',
      operator: '$eq',
      value: selectedTag.value === 'contract' ? DocumentType.CONTRACT : undefined,
    })
    .build();

  const { data, isFetching } = useGetDocumentsQuery(queryString, {
    skip: selectedTag.value === 'shared',
  });
  const { data: selfData, isFetching: selfDocumentsIsFetching } = useGetSelfDocumentsQuery(queryString, {
    skip: selectedTag.value !== 'shared',
  });

  const documents = selectedTag.value === 'shared' ? selfData?.data : data?.data;
  const supportedDocTypes = useAppSelector(selectSupportedDocTypes);
  const isSalesforceAuth = useAppSelector(selectIsSalesforceAuth);

  const getTagOptions = () => {
    const defaultTagOptions = [
      {
        value: 'all',
        label: 'All Files',
      },
      {
        value: 'recentlyOpened',
        label: 'Recently Opened',
      },
      {
        value: 'favorites',
        label: 'Favorites',
      },
    ];

    const variableTagOptions = [
      {
        value: 'document',
        label: 'Documents',
      },
      {
        value: 'contract',
        label: 'Contracts',
      },
      {
        value: 'quote',
        label: 'Quotes',
      },
    ];

    if (isSalesforceAuth && supportedDocTypes) {
      const supportedDocTypesLowerCase = supportedDocTypes.map(type => type.toLowerCase());
      const filteredTagOptions = variableTagOptions.filter(item =>
        supportedDocTypesLowerCase.includes(item.value.toLowerCase())
      );

      return [...defaultTagOptions, ...filteredTagOptions];
    }
    return defaultTagOptions;
  };

  const openDetailsModal = () => {
    setIsDetailsModalOpen(true);
  };

  const openModal = (data: { id: number; name: string }) => {
    setModalData(data);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const closeDetailsModal = () => {
    setIsDetailsModalOpen(false);
  };

  const deleteModalProps = {
    onClose: closeModal,
    modalData: modalData,
  };

  const renameModalProps = {
    onClose: closeModal,
    modalData: modalData,
  };

  const modalHandler = () => {
    switch (modalType) {
      case 'delete':
        return <ModalFactory type='delete' props={deleteModalProps} />;

      case 'rename':
        return <ModalFactory type='rename' props={renameModalProps} />;

      case 'duplicate':
        return <ModalFactory type='duplicate' props={renameModalProps} />;

      default:
        return null;
    }
  };

  const documentMenuItems = (id: number, name: string, status: string): MenuOption[] => {
    const isDocumentInDocusign = (status: string) => {
      switch (status) {
        case documentStatus.Sent:
        case documentStatus.Viewed:
        case documentStatus.Signed:
        case documentStatus.Inactive:
          return true;
        default:
          return false;
      }
    };
    const options = [
      {
        item: (
          <Grid container justifyContent='flex-start' gap={1} width='264px'>
            <DescriptionRounded sx={{ color: docGeniusPalette.primaryBlue }} /> Rename
          </Grid>
        ),
        handler: () => {
          setModalType!('rename');
          openModal({ id, name });
        },
      },
      {
        item: (
          <Grid container justifyContent='flex-start' gap={1} width='264px'>
            <FileCopy sx={{ color: docGeniusPalette.primaryBlue }} /> Duplicate
          </Grid>
        ),
        handler: () => {
          setModalType!('duplicate');
          openModal({ id, name });
        },
      },
      {
        item: (
          <Grid container justifyContent='flex-start' gap={1} width='264px'>
            <Delete sx={{ color: docGeniusPalette.primaryBlue }} /> Delete
          </Grid>
        ),
        handler: () => {
          setModalType!('delete');
          openModal({ id, name });
        },
      },
    ];

    if (isDocumentInDocusign(status)) {
      options.unshift({
        item: (
          <Grid container justifyContent='flex-start' gap={1} width='264px'>
            <Visibility sx={{ color: docGeniusPalette.primaryBlue }} /> View Details
          </Grid>
        ),
        handler: () => {
          setDocumentInfo({ id, documentName: name });
          openDetailsModal();
        },
      });
    }
    return options;
  };

  const handleOnClickNewFile = () => {
    navigate('/documents/create/document');
  };

  const renderDocuments = () => {
    if (isFetching || selfDocumentsIsFetching) {
      return (
        <Fragment>
          {!isList ? (
            <Box display='flex' alignItems={'center'} gap={'24px'}>
              <AzulSkeleton height={334} width={264} variant='rounded' />
              <AzulSkeleton height={334} width={264} variant='rounded' />
              <AzulSkeleton height={334} width={264} variant='rounded' />
              <AzulSkeleton height={334} width={264} variant='rounded' />
            </Box>
          ) : (
            <AzulSkeleton height={219} variant='rounded' />
          )}
        </Fragment>
      );
    }

    if (!documents?.length) {
      return (
        <EmptyTable
          title='There is nothing here!'
          subtitle='Start creating new files to get started'
          buttonText='New File'
          onClick={handleOnClickNewFile}
        />
      );
    }
    return !isList ? (
      <CardListPreview
        data={documents}
        menuItems={documentMenuItems}
        toggleFavorite={toggleFavorite}
        handleOpen={handleOpenDocument}
      />
    ) : (
      <DocumentTable data={documents} menuItems={documentMenuItems} />
    );
  };

  return (
    <Fragment>
      {isModalOpen && modalHandler()}
      {isDetailsModalOpen && (
        <DetailsModal documentInfo={documentInfo} open={isDetailsModalOpen} onClose={closeDetailsModal} />
      )}
      <Grid container xs={12} justifyContent='center'>
        <Grid container className={styles.container} xs={12} md={8} sm={12}>
          <Grid container justifyContent='space-between' alignItems='center'>
            <Grid item>
              <Typography
                variant='h4'
                gutterBottom
                color={docGeniusPalette.primaryBlue.toString()}
                align='left'
                fontSize={'24px'}
                fontWeight={500}
              >
                My Files
              </Typography>
            </Grid>
            <Grid item>
              <NewFileButton />
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <SearchBar placeholder='Search file' value={searchBy} onSearch={handleSearch} />
          </Grid>
          <Grid container justifyContent='space-between' alignItems='center'>
            <Grid item>
              <RadioTag selected={selectedTag} handleChange={onHandleTagChange} options={getTagOptions()} />
            </Grid>
            <Grid item>
              <Tooltip title='Grid View' arrow>
                <IconButton onClick={() => setIsList(false)} sx={{ color: isList ? '#B3C2DA' : '#265183' }}>
                  <GridViewRounded />
                </IconButton>
              </Tooltip>
              <Tooltip title='List View' arrow>
                <IconButton onClick={() => setIsList(true)} sx={{ color: !isList ? '#B3C2DA' : '#265183' }}>
                  <ViewList />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
          {renderDocuments()}
        </Grid>
      </Grid>
    </Fragment>
  );
}
